import { useState, useEffect } from "react";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { KeyRound } from "lucide-react";

/**
 * Change password modal. If `forced` is true (must_change_password on the user),
 * the modal cannot be dismissed until the password is changed.
 */
export function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
  const { setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCurrent(""); setNext(""); setConfirm(""); }
  }, [open]);

  const submit = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      // Refresh user so must_change_password flag clears
      const { data } = await api.get("/auth/me");
      setUser(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Change failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent className="max-w-md bg-card border-border" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            {forced ? "Set a new password" : "Change password"}
          </DialogTitle>
        </DialogHeader>
        {forced && (
          <div className="text-xs p-2 rounded border border-amber-500/30 bg-amber-500/5 text-amber-200/90">
            For security, please change your temporary password before continuing.
          </div>
        )}
        <div className="space-y-3">
          <Field label="Current password">
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" data-testid="cp-current" />
          </Field>
          <Field label="New password" hint="Minimum 8 characters">
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="cp-new" />
          </Field>
          <Field label="Confirm new password">
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="cp-confirm" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          {!forced && <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>}
          <Button onClick={submit} disabled={busy || !current || !next || !confirm} data-testid="cp-submit">
            {busy ? "Saving…" : "Update password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}
